import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/datetime";
import { Stat, Card, PermBadge, errMsg } from "@/components/email/EmParts";

function BackfillSummary({ run }) {
  if (!run) return <p className="text-sm text-slate-500" data-testid="em-overview-no-backfill">No backfill has been run yet.</p>;
  const c = run.counts || {};
  return (
    <div className="text-sm text-slate-600 space-y-1" data-testid="em-overview-last-backfill">
      <p><b>{run.dry_run ? "Dry-run" : "Backfill"}</b> · {formatDateTime(run.finished_at)} · by {run.actor}</p>
      <p>{(c.to_create || 0).toLocaleString()} new · {(c.already_linked || 0).toLocaleString()} already linked · {c.to_adopt || 0} adopted · {c.excluded_staff || 0} staff excluded · {c.errors || 0} errors</p>
      {run.verification && <p className={run.verification.ok ? "text-emerald-700" : "text-amber-700"}>{run.verification.ok ? "Verification passed: every customer has exactly one subscriber" : `Verification: ${run.verification.customers_without_subscriber} missing, ${run.verification.orphan_subscribers} orphans`}</p>}
    </div>
  );
}

export default function EmOverview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/email-marketing/overview").then((r) => setD(r.data)).catch((e) => setD({ error: errMsg(e) })); }, []);
  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  if (d.error) return <p className="text-sm text-rose-700" data-testid="em-overview-error">{d.error}</p>;
  return (
    <div className="space-y-6" data-testid="em-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Stat label="All subscribers" value={d.total} testId="em-stat-total" hint={`${d.linked.toLocaleString()} customers · ${d.standalone} standalone`} />
        <Stat label="Eligible recipients" value={d.eligible} tone="emerald" testId="em-stat-eligible" hint="Consent + valid email + not suppressed" />
        <Stat label="Subscribed (Mailcoach list)" value={d.list_subscribed || 0} tone="indigo" testId="em-stat-subscribed" hint={`${d.subscribed.toLocaleString()} with verified permission`} />
        <Stat label="Unsubscribed" value={d.unsubscribed} tone="rose" testId="em-stat-unsubscribed" />
        <Stat label="Suppressed" value={d.suppressed} tone="rose" testId="em-stat-suppressed" hint={`${d.suppression_list} addresses on list`} />
        <Stat label="No / invalid email" value={d.missing_email + d.invalid_email} tone="amber" testId="em-stat-no-email" hint={`${d.missing_email} missing · ${d.invalid_email} invalid`} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Marketing permission" testId="em-overview-permissions">
          <ul className="space-y-2">
            {["granted", "soft_opt_in", "unknown", "no_consent", "withdrawn"].map((k) => (
              <li key={k} className="flex items-center justify-between text-sm">
                <PermBadge value={k} />
                <Link to={`/email-marketing/subscribers?permission=${k}`} className="tabular-nums font-semibold text-slate-700 hover:text-indigo-700" data-testid={`em-perm-count-${k}`}>{(d.by_permission[k] || 0).toLocaleString()}</Link>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-slate-400 mt-3">Having a subscriber record does not mean consent. Only "Consent granted" and "Soft opt-in" with a valid, unsuppressed email can receive marketing.</p>
        </Card>
        <Card title="Customer sync" testId="em-overview-sync" right={<Link to="/email-marketing/settings" className="text-xs font-semibold text-indigo-600 hover:underline" data-testid="em-overview-settings-link">Manage</Link>}>
          <p className="flex items-center gap-2 text-sm mb-3" data-testid="em-overview-sync-status">
            {d.sync_enabled ? <><ShieldCheck className="w-4 h-4 text-emerald-600" /> Automatic sync is on</> : <><RefreshCw className="w-4 h-4 text-slate-400" /> Automatic sync is off for this company</>}
            {d.last_sweep_at && <span className="text-xs text-slate-400">· last check {formatDateTime(d.last_sweep_at)}</span>}
          </p>
          <BackfillSummary run={d.last_backfill} />
        </Card>
      </div>
    </div>
  );
}
